import React, { useState } from 'react';
import { useGetLinkedInSharingDataMutation, useTrackLinkedInShareMutation } from '../../slices/badgeApi';
import { getLocalVerificationUrl } from '../../constants';
import './LinkedInShareButton.css';

const LinkedInShareButton = ({ 
  badge, 
  digitalBadge, 
  onSharingSuccess, 
  onSharingError,
  className = '',
  variant = 'primary', // 'primary', 'secondary', 'icon-only'
  showInstructions = true 
}) => {
  const [getLinkedInData, { isLoading: isGettingLinkedIn }] = useGetLinkedInSharingDataMutation();
  const [trackLinkedInShare] = useTrackLinkedInShareMutation();
  const [showInstructionsModal, setShowInstructionsModal] = useState(false);
  const [sharingData, setSharingData] = useState(null);
  const [sharing, setSharing] = useState(false);

  const handleShare = async () => {
    if (!digitalBadge?.credential_id) {
      onSharingError?.('No digital badge credential available for sharing');
      return;
    }

    setSharing(true);

    try {
        // If this is a freshly created badge, wait a moment for backend to be ready
        if (digitalBadge._freshlyCreated) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }

      // Get LinkedIn sharing data
      const result = await getLinkedInData({ 
          credentialId: digitalBadge.credential_id 
        }).unwrap();

      // Backend returns structured sharing data
      setSharingData(result);

      // Attempt direct LinkedIn sharing
      if (result.direct_share_url) {
          // Open LinkedIn sharing in new tab
          const shareWindow = window.open(
            result.direct_share_url,
            '_blank'
          );

          // Track sharing attempt (only if user is authenticated)
          try {
            await trackLinkedInShare({
              credentialId: digitalBadge.credential_id,
              shareMethod: 'direct'
            }).unwrap();
          } catch (trackError) {
            // Ignore tracking errors for unauthenticated users
            console.warn('Failed to track LinkedIn share (user may not be authenticated):', trackError);
          }

          // Check if window was blocked or closed immediately
          setTimeout(() => {
            if (shareWindow && shareWindow.closed) {
              // Window was likely blocked, show instructions
              if (showInstructions) {
                setShowInstructionsModal(true);
              }
            } else {
              // Sharing successful
              onSharingSuccess?.(result);
            }
          }, 1000);
        } else {
          // No direct URL, show instructions
          if (showInstructions) {
            setShowInstructionsModal(true);
          } else {
            onSharingError?.('LinkedIn sharing not available');
          }
        }
    } catch (error) {
      console.error('Failed to get LinkedIn sharing data:', error);
      
      // Provide more specific error messages
      let errorMessage = 'Failed to prepare LinkedIn sharing. Please try again.';
      
      if (error?.data?.message) {
        errorMessage = error.data.message;
        
        // If badge was just created, suggest waiting
        if (digitalBadge._freshlyCreated && error.data.message.includes('not found')) {
          errorMessage = 'Badge is still being processed. Please wait a moment and try again.';
        }
      } else if (error?.status === 404) {
        errorMessage = 'Badge credential not found. Please refresh the page and try again.';
      } else if (error?.status >= 500) {
        errorMessage = 'Server error. Please try again in a few moments.';
      }
      
      onSharingError?.(errorMessage);
    } finally {
      setSharing(false);
    }
  };

  const handleCloseInstructions = () => {
    setShowInstructionsModal(false);
    setSharingData(null);
  };

  const handleCopyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      // Could add a toast notification here
    }).catch(err => {
      console.error('Failed to copy to clipboard:', err);
    });
  };

  const isShared = digitalBadge?.linkedin_shared_date;
  const buttonText = isShared ? 'Share Again' : 'Share to LinkedIn';
  const isDisabled = isGettingLinkedIn || sharing || !digitalBadge?.credential_id;

  // Different button variants
  const getButtonContent = () => {
    if (isGettingLinkedIn || sharing) {
      return (
        <>
          <span className="linkedin-share-button__spinner">⏳</span>
          {variant !== 'icon-only' && 'Preparing...'}
        </>
      );
    }

    switch (variant) {
      case 'icon-only':
        return <span className="linkedin-share-button__icon">💼</span>;
      case 'secondary':
        return (
          <>
            <span className="linkedin-share-button__iconWrap" aria-hidden="true">
              <svg viewBox="0 0 24 24" role="img" aria-hidden="true" width="20" height="20">
                <title>LinkedIn</title>
                <path d="M4.98 3.5C4.98 4.88 3.86 6 2.5 6S0 4.88 0 3.5 1.12 1 2.5 1s2.48 1.12 2.48 2.5zM0 8h5v16H0V8zm7.5 0h4.8v2.18h.07c.67-1.27 2.3-2.6 4.73-2.6 5.06 0 6 3.33 6 7.66V24h-5v-6.93c0-1.65-.03-3.77-2.3-3.77-2.3 0-2.66 1.8-2.66 3.65V24h-5V8z"/>
              </svg>
            </span>
            <span className="linkedin-share-button__divider" aria-hidden="true" />
            <span className="linkedin-share-button__text">{buttonText}</span>
          </>
        );
      default: // primary
        return (
          <>
            <span className="linkedin-share-button__iconWrap" aria-hidden="true">
              <svg viewBox="0 0 24 24" role="img" aria-hidden="true" width="20" height="20">
                <title>LinkedIn</title>
                <path fill="currentColor" d="M4.98 3.5C4.98 4.88 3.86 6 2.5 6S0 4.88 0 3.5 1.12 1 2.5 1s2.48 1.12 2.48 2.5zM0 8h5v16H0V8zm7.5 0h4.8v2.18h.07c.67-1.27 2.3-2.6 4.73-2.6 5.06 0 6 3.33 6 7.66V24h-5v-6.93c0-1.65-.03-3.77-2.3-3.77-2.3 0-2.66 1.8-2.66 3.65V24h-5V8z"/>
              </svg>
            </span>
            <span className="linkedin-share-button__divider" aria-hidden="true" />
            <span className="linkedin-share-button__text">{buttonText}</span>
          </>
        );
    }
  };

  return (
    <>
      <button
        className={`linkedin-share-button linkedin-share-button--${variant} ${className}`}
        onClick={handleShare}
        disabled={isDisabled}
        title={variant === 'icon-only' ? buttonText : ''}
        aria-label={`Share ${badge?.name || 'badge'} to LinkedIn`}
      >
        {getButtonContent()}
      </button>

      {/* LinkedIn Sharing Instructions Modal */}
      {showInstructionsModal && sharingData && (
        <div className="linkedin-share-modal-overlay" onClick={handleCloseInstructions}>
          <div className="linkedin-share-modal" onClick={(e) => e.stopPropagation()}>
            <div className="linkedin-share-modal__header">
              <h3>Share Your Badge to LinkedIn</h3>
              <button 
                className="linkedin-share-modal__close"
                onClick={handleCloseInstructions}
                aria-label="Close modal"
              >
                ×
              </button>
            </div>
            
            <div className="linkedin-share-modal__content">
              <div className="linkedin-share-modal__badge-info">
                <div className="linkedin-share-modal__badge-preview">
                  <div className="badge-preview__icon">
                    {badge?.badge_type === 'fluency' && '⭐'}
                    {badge?.badge_type === 'engagement' && '⭐'}
                    {badge?.badge_type === 'impact' && '⭐'}
                    {(!badge?.badge_type || !['fluency', 'engagement', 'impact'].includes(badge.badge_type)) && '⭐'}
                  </div>
                  <div className="badge-preview__info">
                    <h4>{badge?.name}</h4>
                    <p>Issued by ClassGenius</p>
                  </div>
                </div>
              </div>

              <div className="linkedin-share-modal__instructions">
                <h4>LinkedIn Sharing Instructions</h4>
                <p>Follow these steps to add your verified badge to your LinkedIn profile:</p>
                
                <ol className="linkedin-instructions-list">
                  <li>Go to your <a href="https://www.linkedin.com/in/me/" target="_blank" rel="noopener noreferrer">LinkedIn profile</a></li>
                  <li>Click <strong>"Add profile section"</strong> → <strong>"Licenses & certifications"</strong></li>
                  <li>Fill in the form with the following information:</li>
                </ol>

                <div className="linkedin-form-data">
                  <div className="form-field">
                    <label><strong>Name:</strong></label>
                    <div className="form-field__value">
                      <span>{sharingData?.form_data?.name || badge?.name}</span>
                      <button 
                        className="copy-button"
                        onClick={() => handleCopyToClipboard(sharingData?.form_data?.name || badge?.name)}
                        title="Copy to clipboard"
                      >
                        📋
                      </button>
                    </div>
                  </div>

                  <div className="form-field">
                    <label><strong>Issuing Organization:</strong></label>
                    <div className="form-field__value">
                      <span>{sharingData?.form_data?.issuing_organization || 'ClassGenius'}</span>
                      <button 
                        className="copy-button"
                        onClick={() => handleCopyToClipboard(sharingData?.form_data?.issuing_organization || 'ClassGenius')}
                        title="Copy to clipboard"
                      >
                        📋
                      </button>
                    </div>
                  </div>

                  <div className="form-field">
                    <label><strong>Issue Date:</strong></label>
                    <div className="form-field__value">
                      <span>{sharingData?.form_data?.issue_date || new Date(digitalBadge?.issued_date).toLocaleDateString()}</span>
                      <button 
                        className="copy-button"
                        onClick={() => handleCopyToClipboard(sharingData?.form_data?.issue_date || new Date(digitalBadge?.issued_date).toLocaleDateString())}
                        title="Copy to clipboard"
                      >
                        📋
                      </button>
                    </div>
                  </div>

                  <div className="form-field">
                    <label><strong>Credential ID:</strong></label>
                    <div className="form-field__value">
                      <span className="credential-id">{sharingData?.form_data?.credential_id || digitalBadge?.credential_id}</span>
                      <button 
                        className="copy-button"
                        onClick={() => handleCopyToClipboard(sharingData?.form_data?.credential_id || digitalBadge?.credential_id)}
                        title="Copy to clipboard"
                      >
                        📋
                      </button>
                    </div>
                  </div>

                  <div className="form-field">
                    <label><strong>Credential URL:</strong></label>
                    <div className="form-field__value">
                      <span className="credential-url">{sharingData?.form_data?.credential_url || getLocalVerificationUrl(digitalBadge?.verification_url)}</span>
                      <button 
                        className="copy-button"
                        onClick={() => handleCopyToClipboard(sharingData?.form_data?.credential_url || getLocalVerificationUrl(digitalBadge?.verification_url))}
                        title="Copy to clipboard"
                      >
                        📋
                      </button>
                    </div>
                  </div>
                </div>

                <div className="linkedin-verification-note">
                  <div className="verification-icon">🛡️</div>
                  <div className="verification-text">
                    <strong>Verification Note:</strong> This credential is cryptographically verified and complies with Open Badges 3.0 standards.
                  </div>
                </div>
              </div>

              <div className="linkedin-share-modal__actions">
                <button 
                  className="linkedin-share-modal__action linkedin-share-modal__action--primary"
                  onClick={() => window.open('https://www.linkedin.com/in/me/', '_blank')}
                >
                  Open LinkedIn Profile
                </button>
                <button 
                  className="linkedin-share-modal__action linkedin-share-modal__action--secondary"
                  onClick={handleCloseInstructions}
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LinkedInShareButton;
